import { ConfirmActionDialog } from "@/components/ui/confirm-action-dialog";
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { UpdateUserSchema, useRemoveSupervisorFromMentorships } from "@/lib/api/user.ts";
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";

interface SupervisorToggleProps {
  form: UseFormReturn<UpdateUserSchema>;
  handleUpdate: (field: keyof UpdateUserSchema, value: UpdateUserSchema[keyof UpdateUserSchema]) => void;
  userId: string;
  disabled?: boolean;
}

export function SupervisorToggle({
  form,
  handleUpdate,
  userId,
  disabled = false,
}: SupervisorToggleProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const removeSupervisorMutation = useRemoveSupervisorFromMentorships();

  const handleToggle = async (value: boolean) => {
    if (disabled) return;
    if (!value) {
      setShowConfirmDialog(true);
    } else {
      form.setValue("is_supervisor", value);
      handleUpdate("is_supervisor", value);
    }
  };

  const handleConfirm = async () => {
    try {
      await removeSupervisorMutation.mutateAsync({ userId });
      form.setValue("is_supervisor", false);
    } catch (error) {
      console.error("Error removing supervisor:", error);
    } finally {
      setShowConfirmDialog(false);
    }
  };

  return (
    <>
      <FormField
        control={form.control}
        name="is_supervisor"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <FormLabel className="text-base">Supervisor</FormLabel>
              <FormDescription>
                Set this admin as a supervisor
              </FormDescription>
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={handleToggle}
                disabled={disabled}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <ConfirmActionDialog
        isOpen={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirm}
        itemName="supervisor status"
        description="Disabling supervisor status will remove this person from all active mentorships they are supervising. Are you sure you want to continue?"
        loading={removeSupervisorMutation.isPending}
      />
    </>
  );
}
